import React, { useEffect, useState } from 'react';
import {
  Paper, Stack, Typography, TextField, FormControlLabel, Switch, Divider, Alert, Box, CircularProgress,
} from '@mui/material';
import { PreviewBreakdown, PreviewResult, RateCard, previewRateCard } from '../../services/rateCardService';
import { slabLabel } from './rateCardForm';

interface Props { draft: RateCard; saved: RateCard | null }

const inr = (n: number) => `₹${(Math.round(n * 100) / 100).toFixed(2)}`;
const num = (s: string, fallback = 0) => (s.trim() === '' || !Number.isFinite(Number(s)) ? fallback : Number(s));

const Row: React.FC<{ label: string; value: number; hint?: string }> = ({ label, value, hint }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
    <Typography variant="body2">{label}{hint && <Typography component="span" variant="caption" color="text.secondary"> · {hint}</Typography>}</Typography>
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{inr(value)}</Typography>
  </Stack>
);

const Lines: React.FC<{ b: PreviewBreakdown }> = ({ b }) => (
  <Box>
    <Row label="Base fare" value={b.baseFare} />
    {b.distanceSlabs.filter((l) => l.km > 0).map((l) => (
      <Row key={l.fromKm} label={`Distance ${slabLabel(l.fromKm, l.toKm)}`} value={l.amount} hint={`${Math.round(l.km * 1000) / 1000} km × ₹${l.perKm}`} />
    ))}
    {b.timeAmount > 0 && <Row label="Time fare" value={b.timeAmount} hint={`${Math.round(b.timeMinutes * 10) / 10} min × ₹${b.timePerMin}`} />}
    {b.pickupCharges > 0 && <Row label="Pickup charge" value={b.pickupCharges} />}
    {b.waitingAmount > 0 && <Row label="Wait time" value={b.waitingAmount} hint={`${b.billableWaitMinutes} min billable`} />}
    {b.longPickupAmount > 0 && <Row label="Long pickup" value={b.longPickupAmount} hint={`${Math.round(b.longPickupKm * 100) / 100} km billable`} />}
    {b.driverAdditions > 0 && <Row label="Driver additions" value={b.driverAdditions} />}
    {b.congestionCharges > 0 && <Row label="Congestion" value={b.congestionCharges} />}
    {b.nightAmount > 0 && <Row label="Night surcharge (included above)" value={b.nightAmount} />}
    {b.minimumFareTopUp > 0 && <Row label="Top-up to minimum fare" value={b.minimumFareTopUp} />}
  </Box>
);

/** Runs the SERVER engine on the draft (and the saved card) — the same maths quotes and bills use. */
const FarePreview: React.FC<Props> = ({ draft, saved }) => {
  const [distance, setDistance] = useState('10');
  const [minutes, setMinutes] = useState('30');
  const [wait, setWait] = useState('0');
  const [pickup, setPickup] = useState('0');
  const [surge, setSurge] = useState('1');
  const [night, setNight] = useState(false);
  const [result, setResult] = useState<{ draft: PreviewResult; saved: PreviewResult | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const draftKey = JSON.stringify(draft);
  const savedKey = JSON.stringify(saved);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const inputs = {
        distanceKm: Math.min(10000, num(distance)), durationMin: Math.min(6000, num(minutes)),
        waitMin: Math.min(1440, num(wait)), pickupKm: Math.min(1000, num(pickup)),
        isNight: night, surgeFactor: Math.min(10, Math.max(1, num(surge, 1))),
      };
      setLoading(true);
      try {
        const [d, s] = await Promise.all([
          previewRateCard({ card: JSON.parse(draftKey), ...inputs }),
          savedKey !== 'null' ? previewRateCard({ card: JSON.parse(savedKey), ...inputs }) : Promise.resolve(null),
        ]);
        if (!cancelled) setResult({ draft: d, saved: s });
      } catch {
        if (!cancelled) setResult({ draft: { ok: false, errors: ['Preview unavailable — check your connection'] }, saved: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [draftKey, savedKey, distance, minutes, wait, pickup, surge, night]);

  const d = result?.draft;
  const s = result?.saved;
  return (
    <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 80 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Live preview</Typography>
        {loading && <CircularProgress size={14} />}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField size="small" label="Distance km" type="number" value={distance} inputProps={{ step: 'any', min: 0 }} onChange={(e) => setDistance(e.target.value)} />
        <TextField size="small" label="Trip min" type="number" value={minutes} inputProps={{ step: 'any', min: 0 }} onChange={(e) => setMinutes(e.target.value)}
                   helperText="booked route time" />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField size="small" label="Wait min" type="number" value={wait} inputProps={{ step: 'any', min: 0 }} onChange={(e) => setWait(e.target.value)} />
        <TextField size="small" label="Pickup km" type="number" value={pickup} inputProps={{ step: 'any', min: 0 }} onChange={(e) => setPickup(e.target.value)} />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TextField size="small" label="Surge ×" type="number" value={surge} onChange={(e) => setSurge(e.target.value)} inputProps={{ step: '0.05', min: 1 }} />
      </Stack>
      <FormControlLabel control={<Switch checked={night} onChange={(e) => setNight(e.target.checked)} />} label="Night ride" />
      <Divider sx={{ my: 1.5 }} />
      {d && !d.ok && <Alert severity="warning" sx={{ mb: 1 }}>{d.errors.map((e) => <div key={e}>{e}</div>)}</Alert>}
      {d && d.ok && (
        <>
          <Lines b={d.breakdown} />
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Rider pays{d.surgeFactor > 1 ? ` (× ${d.surgeFactor} surge)` : ''}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>₹{d.quotedFare}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">Only the total is rounded, to the nearest ₹1. Tips are added on top.</Typography>
          {s && s.ok && s.quotedFare !== d.quotedFare && (
            <Alert severity="info" sx={{ mt: 1 }}>Saved card for the same ride: ₹{s.quotedFare} → this draft: ₹{d.quotedFare}</Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default FarePreview;
